import { Route, RouteConfig } from 'vue-router';
import { ScheduleLoadsTab } from '@/constants/export/load-plan';
import { PermissionsImportFeatures, PermissionsIpiLoadingFunctions } from '@/constants/permissions';

interface GenerateHblPageOptions {
  hasBreadcrumbs?: boolean;
}

export const generateHblPage = (
  name: string,
  options: GenerateHblPageOptions = {}
): RouteConfig => {
  const { hasBreadcrumbs = false } = options;

  const hblPageRoute = hasBreadcrumbs ? ':houseId' : 'house-bl/view/:houseId';

  const generatedHblPageOption = hblPageRouteOption(name, hblPageRoute);

  return hasBreadcrumbs ? hblPageBreadcrumbWrapper(generatedHblPageOption) : generatedHblPageOption;
};

const hblPageBreadcrumbWrapper = (route: RouteConfig): RouteConfig => ({
  component: () => import('@/layouts/breadcrumb/BreadcrumbLayout.vue'),
  path: 'house-bl/view',
  children: [route],
});

const hblPageRouteOption = (name: string, path: string) => ({
  name,
  path,
  meta: {
    pageTitle: 'HBL',
    dynamicTitle: true,
    disableWarehouseSwitch: true,
    breadcrumb: {
      label: 'House BL',
      routeName: name,
    },
  },
  props: (route: Route) => ({
    id: route.params.houseId,
  }),
  component: () => import('@/views/house-bl/house-bl-view/HouseBLView.desktop.vue'),
});

export const scheduleRoutes = [
  {
    path: 'ipi',
    meta: {
      breadcrumb: {
        label: 'Calendar',
        routeName: 'IpiCalendar',
      },
    },
    component: () => import('@/layouts/EmptyRouterView.vue'),
    children: [
      {
        name: 'IpiCalendar',
        meta: {
          pageTitle: 'IPI Loading',
          permission: PermissionsImportFeatures.IPI_LOADING,
        },
        path: '',
        component: () => import('@/views/schedule/dashboard/DashboardScheduleVirtual.vue'),
        children: [
          {
            name: 'InventoryDrawer',
            meta: {
              permission: PermissionsImportFeatures.INVENTORY,
              breadcrumb: {
                label: 'Inventory',
                routeName: 'InventoryDrawer',
                exact: false,
              },
              pageTitle: 'Inventory',
            },
            path: 'inventory',
          },
          {
            path: 'load/plan/:loadPlanId',
            meta: {
              permission: PermissionsImportFeatures.LOAD_PLAN,
              pageTitle: 'Load Plan',
              breadcrumb: {
                label: 'Load Plan',
                routeName: 'LoadPlanDrawer',
                exact: false,
              },
            },
            name: 'LoadPlanDrawer',
          },
          {
            name: 'InventoryDrawerExpanded',
            meta: {
              permission: PermissionsImportFeatures.INVENTORY,
              breadcrumb: {
                label: 'Inventory Expanded',
                routeName: 'InventoryDrawerExpanded',
              },
              pageTitle: 'InventoryDrawerExpanded',
            },
            path: 'inventory/expanded',
          },
        ],
      },
      {
        path: 'load/plan/expanded/:loadPlanId',
        component: () => import('@/layouts/breadcrumb/BreadcrumbLayout.vue'),
        meta: {
          permission: PermissionsImportFeatures.LOAD_PLAN,
          breadcrumb: {
            label: (params: Record<string, string>) => `Load Plan Expanded ${params.loadPlanId}`,
            routeName: 'LoadPlanDrawerExpanded',
          },
          pageTitle: 'LoadPlanDrawerExpanded',
        },
        children: [generateHblPage('LoadPlanDrawerExpandedHBL')],
      },
      {
        path: 'load/plan/:loadPlanId',
        meta: {
          permission: PermissionsImportFeatures.LOAD_PLAN,
          pageTitle: 'LoadPlanDrawer',
          breadcrumb: {
            label: 'Load Plan',
            routeName: 'LoadPlanDrawer',
            exact: false,
          },
        },
        component: () => import('@/layouts/breadcrumb/BreadcrumbLayout.vue'),
        children: [generateHblPage('LoadPlanDrawerHBL')],
      },
      {
        meta: {
          permission: PermissionsImportFeatures.INVENTORY,
          breadcrumb: {
            label: 'Inventory',
            routeName: 'InventoryDrawer',
          },
        },
        path: 'inventory',
        component: () => import('@/layouts/breadcrumb/BreadcrumbLayout.vue'),
        children: [generateHblPage('InventoryDrawerHBL')],
      },
      {
        meta: {
          permission: PermissionsImportFeatures.INVENTORY,
          breadcrumb: {
            label: 'Inventory Expanded',
            routeName: 'InventoryDrawerExpanded',
          },
        },
        path: 'inventory/expanded',
        component: () => import('@/layouts/breadcrumb/BreadcrumbLayout.vue'),
        children: [generateHblPage('InventoryDrawerExpandedHBL')],
      },
      {
        path: 'loads',
        meta: {
          permission: PermissionsIpiLoadingFunctions.IPI_LOADS,
          breadcrumb: {
            label: 'Load Plan & Load Guide View',
            routeName: 'IpiLoadGuide',
            query: (query: Record<string, string>) => ({
              loadGuideId: query.loadGuideId,
              tab: ScheduleLoadsTab.LoadGuide,
            }),
          },
        },
        component: () => import('@/layouts/breadcrumb/BreadcrumbLayout.vue'),
        children: [
          {
            name: 'IpiLoadGuide',
            meta: {
              permission: PermissionsImportFeatures.LOAD_GUIDE,
              pageTitle: 'IPI Loads',
            },
            path: '',
            props: (route: Route) => ({ id: route.params.loadPlanId }),
            component: () => import('@/views/schedule/load/VirtualLoadsView.vue'),
          },
          generateHblPage('IpiLoadGuideHBL'),
        ],
      },
      // {
      //   meta: {
      //     permission: PermissionsImportFeatures.IPI_TRACKING,
      //     breadcrumb: {
      //       label: 'Tracking List',
      //       routeName: 'IpiTracking',
      //       query: (query: Record<string, string>) => ({
      //         loadGuideId: query.loadGuideId,
      //       }),
      //     },
      //   },
      //   path: 'tracking',
      //   component: () => import('@/layouts/EmptyRouterView.vue'),
      //   children: [
      //     {
      //       path: '',
      //       name: 'IpiTracking',
      //       meta: {
      //         permission: PermissionsImportFeatures.IPI_TRACKING,
      //         pageTitle: 'IPI Tracking',
      //       },
      //       component: () => import('@/views/tracking/IpiTrackingView.vue'),
      //     },
      //     generateHblPage('IpiTrackingHBL', { hasBreadcrumbs: true }),
      //   ],
      // },
      {
        meta: {
          permission: PermissionsImportFeatures.IPI_RECEIVING,
          breadcrumb: {
            label: 'IPI Receiving',
            routeName: 'IpiReceiving',
            query: (query: Record<string, string>) => ({
              loadGuideId: query.loadGuideId,
            }),
          },
        },
        path: 'receiving',
        component: () => import('@/layouts/EmptyRouterView.vue'),
        children: [
          {
            path: '',
            name: 'IpiReceiving',
            meta: {
              permission: PermissionsImportFeatures.IPI_RECEIVING,
              pageTitle: 'IPI Receiving',
            },
            component: () => import('@/views/receiving/IpiReceivingVirtual.vue'),
          },
          {
            meta: {
              breadcrumb: {
                label: 'MBL',
                routeName: 'IpiReceivingMBL',
              },
            },
            path: 'mbl/:id',
            component: () => import('@/layouts/breadcrumb/BreadcrumbLayout.vue'),
            children: [
              {
                path: '',
                props: true,
                meta: {
                  pageTitle: 'IPI Receiving | MBL',
                },
                name: 'IpiReceivingMBL',
                component: () => import('@/views/master-bl/MasterBLView.vue'),
              },
              {
                path: 'hbl/:houseId',
                props: (route: Route) => ({
                  id: route.params.houseId,
                }),
                meta: {
                  pageTitle: 'IPI Receiving | HBL',
                  breadcrumb: {
                    label: 'HBL',
                    routeName: 'IpiReceivingHBL',
                  },
                },
                name: 'IpiReceivingHBL',
                component: () => import('@/views/house-bl/house-bl-view/HouseBLView.tablet.vue'),
              },
            ],
          },
        ],
      },
    ],
  },
];
